<script setup lang="ts">
import { ref } from "vue";
import draggable from "vuedraggable";
import type { RuleGroup, RuleConditionItem, ServiceCondition } from "@/utils/archival";
import type { AnyRatingService } from "@/stores/ratingService";
import { RatingServiceType } from "@/stores/ratingService";
import ServiceConditionChip from "./ServiceConditionChip.vue";

// Module-level WeakMap so IDs stay stable when items are dragged between groups
const _itemIds = new WeakMap<object, string>();
let _itemIdCounter = 0;
function itemId(item: object): string {
  if (!_itemIds.has(item)) _itemIds.set(item, `ri-${++_itemIdCounter}`);
  return _itemIds.get(item)!;
}

defineOptions({ name: "RuleGroupEditor" });

const props = defineProps<{
  group: RuleGroup;
  availableServices: AnyRatingService[];
  isRoot?: boolean;
}>();

const emit = defineEmits<{
  (e: "remove"): void;
}>();

const showServicePicker = ref(false);
const pickerServiceKey = ref<string>("");

function toggleNegated() {
  props.group.negated = !props.group.negated;
}

function toggleOperator() {
  props.group.operator = props.group.operator === "AND" ? "OR" : "AND";
}

function removeItem(index: number) {
  props.group.items.splice(index, 1);
}

function updateCondition(index: number, updated: ServiceCondition) {
  props.group.items[index] = updated;
}

function addGroup() {
  props.group.items.push({
    type: "group",
    operator: "AND",
    negated: false,
    items: [],
  } as RuleGroup);
}

function openServicePicker() {
  if (props.availableServices.length > 0) {
    pickerServiceKey.value = props.availableServices[0].service_key;
  }
  showServicePicker.value = true;
}

function confirmAddService() {
  const svc = props.availableServices.find((s) => s.service_key === pickerServiceKey.value);
  if (!svc) { showServicePicker.value = false; return; }

  let condition: ServiceCondition;
  if (svc.type === RatingServiceType.Like) {
    condition = {
      type: "service",
      serviceKey: svc.service_key,
      serviceName: svc.name,
      serviceType: "like",
      match: "any",
      negated: false,
    };
  } else {
    condition = {
      type: "service",
      serviceKey: svc.service_key,
      serviceName: svc.name,
      serviceType: svc.type === RatingServiceType.Stars ? "stars" : "counter",
      operator: "≥",
      value: 1,
      negated: false,
    };
  }
  props.group.items.push(condition);
  showServicePicker.value = false;
}

function isServiceCondition(item: RuleConditionItem): item is ServiceCondition {
  return item.type === "service";
}

function isGroupItem(item: RuleConditionItem): item is RuleGroup {
  return item.type === "group";
}
</script>

<template>
  <div class="rule-group" :class="{ 'rule-group--negated': group.negated }">
    <div class="rule-group__header">
      <button
        v-if="!isRoot"
        class="btn-not"
        :class="{ active: group.negated }"
        :aria-pressed="group.negated"
        title="Toggle NOT"
        @click="toggleNegated"
      >¬</button>
      <button
        class="btn-operator"
        :title="`Switch to ${group.operator === 'AND' ? 'OR' : 'AND'}`"
        @click="toggleOperator"
      >{{ group.operator }}</button>
      <button v-if="!isRoot" class="btn-remove" title="Remove group" @click="emit('remove')">✕</button>
    </div>

    <draggable
      v-model="group.items"
      :group="{ name: 'rule-items', pull: true, put: true }"
      :item-key="itemId"
      :animation="150"
      ghost-class="drag-ghost"
      handle=".drag-handle"
      class="rule-group__items"
    >
      <template #item="{ element, index }">
        <div class="rule-item">
          <span class="drag-handle" title="Drag to reorder">⠿</span>
          <ServiceConditionChip
            v-if="isServiceCondition(element)"
            :condition="element"
            @update="updateCondition(index, $event)"
            @remove="removeItem(index)"
          />
          <RuleGroupEditor
            v-else-if="isGroupItem(element)"
            :group="element"
            :available-services="availableServices"
            @remove="removeItem(index)"
          />
        </div>
      </template>
    </draggable>

    <div class="rule-group__actions">
      <button
        class="btn-add-service"
        :disabled="availableServices.length === 0"
        @click="openServicePicker"
      >+ Service</button>
      <button class="btn-add-group" @click="addGroup">+ Group</button>
    </div>

    <div v-if="showServicePicker" class="service-picker" @click.stop>
      <select v-model="pickerServiceKey" class="service-picker__select">
        <option v-for="s in availableServices" :key="s.service_key" :value="s.service_key">{{ s.name }}</option>
      </select>
      <button @click="confirmAddService">Add</button>
      <button @click="showServicePicker = false">Cancel</button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.rule-group {
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 8px;
  margin: 4px 0;
  background: var(--bg-base, var(--bg-elevated));

  &.rule-group--negated {
    border-color: var(--accent);
  }
}

.rule-group__header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.btn-operator {
  background: color-mix(in srgb, var(--accent) 15%, var(--bg-elevated));
  color: var(--accent);
  border: 1px solid var(--accent);
  border-radius: var(--radius-sm, 3px);
  padding: 2px 8px;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.8em;

  &:hover {
    background: color-mix(in srgb, var(--accent) 25%, var(--bg-elevated));
  }
}

.btn-not {
  background: none;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 3px);
  padding: 2px 6px;
  cursor: pointer;
  font-weight: bold;
  color: var(--text-muted, #888);

  &.active {
    color: var(--accent);
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 12%, transparent);
  }

  &:hover {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
  }
}

.btn-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted, #888);
  padding: 2px 5px;
  margin-left: auto;

  &:hover {
    color: var(--danger, #e55);
  }
}

.rule-group__items {
  min-height: 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
}

.rule-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.drag-handle {
  cursor: grab;
  color: var(--text-muted, #aaa);
  font-size: 1.1em;
  padding-top: 3px;
  user-select: none;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
}

.drag-ghost {
  opacity: 0.4;
}

.rule-group__actions {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.btn-add-service, .btn-add-group {
  background: none;
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm, 3px);
  padding: 3px 8px;
  cursor: pointer;
  color: var(--text-secondary, var(--text-primary));
  font-size: 0.82em;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.service-picker {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 6px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);

  select.service-picker__select {
    flex: 1;
    background: var(--bg-elevated);
    color: var(--text-primary);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm, 3px);
    padding: 3px 6px;
  }

  button {
    background: none;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm, 3px);
    padding: 3px 8px;
    cursor: pointer;
    color: var(--text-primary);
    font-size: 0.85em;

    &:first-of-type {
      background: var(--accent);
      color: #fff;
      border-color: var(--accent);
    }

    &:hover:not(:first-of-type) {
      border-color: var(--accent);
    }
  }
}
</style>
